import { format, parseISO, isValid } from 'date-fns';
import {
	CLIENT_DOCUMENT_THEME,
	waitForClientDocumentFonts,
} from '@/utils/clientDocumentTheme';
import { normalizeInvoicePaymentTerms } from '@/utils/invoicePaymentTerms';

const DEFAULT_SECTION_MARGIN = '40px';
const DEFAULT_CELL_PADDING = '10px';
const PAGE_WIDTH_IN = 8.27;
const PAGE_HEIGHT_IN = 11.68;
const PAGE_WIDTH = `${PAGE_WIDTH_IN}in`;
const PAGE_HEIGHT = `${PAGE_HEIGHT_IN}in`;
const SPACING_PRESETS = [
	null,
	{ sectionMargin: '32px', cellVerticalPadding: '8px' },
	{ sectionMargin: '24px', cellVerticalPadding: '6px' },
	{ sectionMargin: '20px', cellVerticalPadding: '4px' }
];
const FOOTER_BUFFER_PX = 32;

export const QUANTITY_PRICING_INVOICE_START_DATE = '2026-08-01';
export const QUANTITY_PRICING_LINE_ITEM_LAYOUT = 'quantity-unit-price';

export const usesQuantityPricingInvoiceLayout = (value, lineItemLayout = '') => {
	if (lineItemLayout === QUANTITY_PRICING_LINE_ITEM_LAYOUT) return true;
	if (value instanceof Date && isValid(value)) {
		return format(value, 'yyyy-MM-dd') >= QUANTITY_PRICING_INVOICE_START_DATE;
	}
	const date = String(value ?? '').trim().slice(0, 10);
	return /^\d{4}-\d{2}-\d{2}$/.test(date)
		&& date >= QUANTITY_PRICING_INVOICE_START_DATE;
};

const isSameSpacing = (a, b) => {
	if (!a && !b) return true;
	if (!a || !b) return false;
	return a.sectionMargin === b.sectionMargin && a.cellVerticalPadding === b.cellVerticalPadding;
};

const waitForImages = async (root) => {
	const images = root ? Array.from(root.querySelectorAll('img')) : [];
	if (!images.length) return;
	await Promise.all(images.map(img => new Promise(resolve => {
		if (img.complete) return resolve();
		const done = () => {
			img.onload = null;
			img.onerror = null;
			resolve();
		};
		img.onload = done;
		img.onerror = done;
		setTimeout(done, 1000);
	})));
};

const normaliseSpacingQueue = (initialSpacing, spacingCandidates = SPACING_PRESETS) => {
	const queue = [];
	const pushUnique = (spacing) => {
		if (queue.some(existing => isSameSpacing(existing, spacing))) return;
		queue.push(spacing || null);
	};
	if (initialSpacing) pushUnique(initialSpacing);
	pushUnique(null);
	spacingCandidates.forEach(pushUnique);
	return queue;
};

const createProbeContainer = () => {
	const probe = document.createElement('div');
	probe.style.position = 'absolute';
	probe.style.left = '-10000px';
	probe.style.top = '-10000px';
	probe.style.width = PAGE_WIDTH;
	probe.style.height = PAGE_HEIGHT;
	probe.style.overflow = 'hidden';
	probe.style.visibility = 'hidden';
	probe.style.pointerEvents = 'none';
	probe.style.zIndex = '-1';
	// Use a shadow root so style tags inside the probe do not leak and momentarily restyle the visible preview
	if (!probe.shadowRoot) {
		probe.attachShadow({ mode: 'open' });
	}
	return probe;
};

const safeDate = (value) => {
	if (!value) return null;
	try {
		const parsed = typeof value === 'string' ? parseISO(value) : new Date(value);
		return isValid(parsed) ? parsed : null;
	} catch (e) {
		return null;
	}
};

const formatDisplayDate = (value) => {
	const parsed = safeDate(value);
	return parsed ? format(parsed, 'dd MMMM yyyy') : '';
};

const formatDateRange = (from, to) => {
	const fromDate = safeDate(from);
	const toDate = safeDate(to);
	if (!fromDate && !toDate) return '';
	if (fromDate && toDate && fromDate.getTime() === toDate.getTime()) {
		return format(fromDate, 'dd MMMM yyyy');
	}
	const start = fromDate ? format(fromDate, 'dd MMMM yyyy') : '';
	const end = toDate ? format(toDate, 'dd MMMM yyyy') : '';
	return [start, end].filter(Boolean).join(' - ');
};

export const buildBusinessInfo = (project) => {
	const settings = project?.businessSettings || {};
	const infoParts = [];
	if (settings.name) infoParts.push(settings.name);
	if (settings.address) infoParts.push(settings.address);
	if (settings.email) infoParts.push(settings.email);
	if (settings.phone) infoParts.push(settings.phone);
	if (settings.regNumber) infoParts.push(`Company Reg. ${settings.regNumber}`);
	if (settings.vatNumber) infoParts.push(`VAT Reg. No. ${settings.vatNumber}`);

	return {
		businessName: settings.name || '',
		businessInfo: infoParts.join('\n'),
		bankDetails: settings.bankDetails || ''
	};
};

const formatGbpAmount = (amount) => {
	const numeric = Number(amount);
	if (Number.isNaN(numeric)) return '£0.00';
	return new Intl.NumberFormat('en-GB', {
		style: 'currency',
		currency: 'GBP',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(numeric);
};

const escapeHtml = (value) => {
	if (value === null || value === undefined) return '';
	return String(value)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');
};

const escapeMultiLine = (text) => {
	if (!text) return '';
	return text
		.split('\n')
		.map(escapeHtml)
		.join('\n');
};

const resolveInvoiceDocumentKind = (invoiceData = {}) => {
	const raw = String(
		invoiceData?.metadata?.documentType || invoiceData?.documentType || 'invoice',
	)
		.trim()
		.toLowerCase();
	return raw === 'credit-note' ? 'credit-note' : 'invoice';
};

const normalizeVatRate = (value) => {
	const numeric = Number(value);
	if (!Number.isFinite(numeric)) return 0.2;
	return numeric === 0 ? 0 : 0.2;
};

const formatBusinessInfoHtml = (info) => {
	if (!info) return '';
	const lines = info.split('\n');
	if (lines.length === 0) return '';
	let html = `<strong>${escapeHtml(lines[0])}</strong>`;
	if (lines.length > 1) {
		html += '\n' + lines.slice(1).map(escapeHtml).join('\n');
	}
	return html;
};

const formatAmountForLegacy = (value) => formatGbpAmount(value);

const resolveItemAmountMode = (item = {}) => {
	const mode = (item?.amountMode || '').toLowerCase();
	if (mode === 'direct' || mode === 'calculated') return mode;
	const directAmount = item?.directAmount;
	if (directAmount !== null && directAmount !== undefined && directAmount !== '') {
		return 'direct';
	}
	return 'calculated';
};

const resolveItemAmountValue = (item = {}) => (
	resolveItemAmountMode(item) === 'direct'
		? (item.directAmount ?? item.amount ?? 0)
		: (item.amount ?? item.directAmount ?? 0)
);

const isAppointmentItem = (item = {}) => {
	if (!item || typeof item !== 'object') return false;
	const sourceType = String(item.sourceType || '').toLowerCase();
	if (sourceType === 'appointments') return true;
	if (sourceType === 'purchases') return false;
	if (item.serviceId) return true;
	const mode = resolveItemAmountMode(item);
	if (mode !== 'direct') return true;
	const hasHours = item.hours !== null && item.hours !== undefined && item.hours !== '';
	const hasRate = item.rate !== null && item.rate !== undefined && item.rate !== '';
	return hasHours || hasRate;
};

const isPurchaseItem = (item = {}) => (
	String(item?.sourceType || '').toLowerCase() === 'purchases'
);

const formatHoursForLegacy = (value, amountMode) => {
	if ((amountMode || '').toLowerCase() === 'direct') return '';
	if (value === null || value === undefined || value === '') return '';
	const num = Number(value);
	if (!Number.isFinite(num)) {
		return String(value);
	}
	const rounded = Number(num.toFixed(2));
	if (Number.isInteger(rounded)) {
		return String(rounded);
	}
	return rounded.toString();
};

const normaliseHoursText = (value, fallback = '') => {
	const candidate = value !== undefined && value !== null && value !== '' ? value : fallback;
	if (candidate === '' || candidate === null || candidate === undefined) return '';
	const num = Number(candidate);
	if (!Number.isFinite(num)) return String(candidate);
	const rounded = Number(num.toFixed(2));
	return Number.isInteger(rounded) ? String(rounded) : rounded.toString();
};

const formatRateForLegacy = (value, amountMode) => {
	if ((amountMode || '').toLowerCase() === 'direct') return '';
	if (value === null || value === undefined || value === '') return '';
	return formatGbpAmount(value);
};

const hasFlatRefColumnData = (row = {}) => {
	if (!row || typeof row !== 'object') return false;
	return row.flatRef !== null && row.flatRef !== undefined && row.flatRef !== '';
};

const hasAppointmentColumnData = (row = {}) => {
	if (!row || typeof row !== 'object') return false;
	const sourceType = String(row.sourceType || '').toLowerCase();
	if (sourceType === 'appointments') return true;
	if (sourceType === 'purchases') return false;
	if (row.serviceId) return true;
	const hasHours = row.hours !== null && row.hours !== undefined && row.hours !== '';
	const hasHoursText = row.hoursText !== null && row.hoursText !== undefined && row.hoursText !== '';
	const hasRate = row.rate !== null && row.rate !== undefined && row.rate !== '';
	const hasRateText = row.rateText !== null && row.rateText !== undefined && row.rateText !== '';
	return hasHours || hasHoursText || hasRate || hasRateText;
};

const hasPurchaseColumnData = (row = {}) => (
	String(row?.sourceType || '').toLowerCase() === 'purchases'
	|| row?.quantityText === '1'
);

const parsePx = (value) => {
	if (value === null || value === undefined || value === '') return null;
	const num = Number.parseFloat(String(value).replace('px', '').trim());
	return Number.isFinite(num) ? num : null;
};

const scoreSpacing = (spacing) => {
	if (!spacing) return Number.POSITIVE_INFINITY;
	const margin = parsePx(spacing.sectionMargin);
	const padding = parsePx(spacing.cellVerticalPadding);
	return (margin ?? Number.POSITIVE_INFINITY) * 1000 + (padding ?? Number.POSITIVE_INFINITY);
};

const pickMostCompactSpacing = (spacingCandidates = []) => {
	let best = null;
	let bestScore = Number.POSITIVE_INFINITY;
	spacingCandidates.forEach((candidate) => {
		if (!candidate) return;
		const score = scoreSpacing(candidate);
		if (score < bestScore) {
			best = candidate;
			bestScore = score;
		}
	});
	return best;
};

const buildInvoiceModel = ({
	invoice,
	projectInfo,
	businessLogoUrl,
	logoUrl,
	snapshot,
	hideTotalTerms = false,
}) => {
	// Handle data from various structures
	const invoiceData = invoice?.data || invoice || {};
	const content = invoiceData.invoiceContent || {};
	const summary = content.summary || {};
	const clientSnapshot = snapshot || invoiceData.clientSnapshot || {};

	// Determine logo URL
	const effectiveLogoUrl = businessLogoUrl || logoUrl;
	const documentKind = resolveInvoiceDocumentKind(invoiceData);
	const isCreditNote = documentKind === 'credit-note';
	const documentLabel = isCreditNote ? 'Credit Note' : 'Invoice';
	const numberLabel = isCreditNote ? 'Credit note number' : 'Invoice number';
	const dateLabel = isCreditNote ? 'Credit note date' : 'Invoice date';
	const showDueDate = !isCreditNote;
	const dueDateLabel = 'Due date';
	const purchaseOrderLabel = 'Purchase order';

	const invoiceNumber = invoiceData.invoiceNumber || invoice?.name || '';
	const formattedInvoiceDate = formatDisplayDate(invoiceData.invoiceDate);
	const formattedDueDate = showDueDate ? formatDisplayDate(invoiceData.dueDate) : '';
	const purchaseOrder = String(invoiceData.purchaseOrder ?? '').trim();
	const showPurchaseOrder = showDueDate && Boolean(purchaseOrder);
	const useQuantityPricingColumns = usesQuantityPricingInvoiceLayout(
		invoiceData.invoiceDate,
		invoiceData.metadata?.lineItemLayout,
	);

	// Handle totals from new structure or legacy
	const subtotal = formatGbpAmount(summary.subtotal ?? invoiceData.subtotal);
	const vat = formatGbpAmount(summary.vat ?? invoiceData.vat);
	const total = formatGbpAmount(summary.total ?? invoiceData.total);
	const vatRate = normalizeVatRate(content.vatRate ?? invoiceData.vatRate);
	const vatLabel = vatRate === 0
		? 'VAT (outside the scope of VAT)'
		: `VAT (${Math.round(vatRate * 100)}%)`;

	const notes = invoiceData.notes || '';

	let formattedTerms = normalizeInvoicePaymentTerms(
		clientSnapshot.terms || invoiceData.formattedTerms || '',
	);
	if (/^due\s(?!on\b)/i.test(formattedTerms)) {
		formattedTerms = formattedTerms.replace(/^due\s+/i, 'due on ');
	}
	const termsIsNumber = formattedTerms && /^[0-9]+$/.test(formattedTerms);

	let totalTermsText = `${total}`;
	if (isCreditNote) {
		totalTermsText = `Credit total ${total}`;
	} else if (termsIsNumber && formattedDueDate) {
		// Present as "£X due on <date>" when terms is just a number of days
		totalTermsText += ` due on ${escapeHtml(formattedDueDate)}`;
	} else if (formattedTerms) {
		totalTermsText += ` ${escapeHtml(formattedTerms)}`;
	} else if (formattedDueDate) {
		totalTermsText += ` due on ${escapeHtml(formattedDueDate)}`;
	}

	if (hideTotalTerms) {
		totalTermsText = '';
	}

	// Item Processing
	let groupsForRender = [];
	const dateRanges = content.dateRanges || [];
	const customDates = content.customDates || [];
	const hasAppointmentItems = (entries = []) =>
		entries.some((entry) => (entry.items || []).some(isAppointmentItem));
	const hasPurchaseItems = (entries = []) =>
		entries.some((entry) => (entry.items || []).some(isPurchaseItem));
	let showAppointmentColumns =
		hasAppointmentItems(dateRanges) || hasAppointmentItems(customDates);
	let showPurchaseColumns =
		hasPurchaseItems(dateRanges) || hasPurchaseItems(customDates);
	let showFlatRefColumn = false;

	// Building Name
	const buildingScope = content.buildingScope || invoiceData.buildingScope || 'single';
	const buildingBase = clientSnapshot.buildingName || invoiceData.buildingName || '';
	const resolvedBuildingName =
		buildingBase && buildingBase !== 'No Building Selected' && buildingBase !== 'Selected Building'
			? buildingBase
			: '';
	const buildGroupHeader = (buildingName, label) => {
		const parts = [];
		if (buildingName) parts.push(buildingName);
		if (label) parts.push(label);
		return parts.join(' / ');
	};
	const compareBuildingNames = (a, b) => {
		const nameA = (a || '').trim();
		const nameB = (b || '').trim();
		if (!nameA && !nameB) return 0;
		if (!nameA) return 1;
		if (!nameB) return -1;
		return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
	};
	const groupRowsByBuilding = (rows = []) => {
		const grouped = new Map();
		rows.forEach((row) => {
			const key = row.buildingName || '';
			if (!grouped.has(key)) {
				grouped.set(key, []);
			}
			grouped.get(key).push(row);
		});
		return grouped;
	};
	const sortGroupedRowsByBuildingName = (groupedRows) => {
		return Array.from(groupedRows.entries()).sort(([a], [b]) => compareBuildingNames(a, b));
	};

	// Convert Date Ranges to Groups
	if (dateRanges.length > 0) {
		dateRanges.forEach((range) => {
			const rows = (range.items || []).map((item) => {
				const mode = resolveItemAmountMode(item);
				const purchase = isPurchaseItem(item);
				const amountText = formatAmountForLegacy(resolveItemAmountValue(item));
				return {
					description: item.description || item.service || item.vendorName || '',
					flatRef: item.flatRef || '',
					hoursText: formatHoursForLegacy(item.hours, mode),
					quantityText: useQuantityPricingColumns || purchase ? '1' : '',
					rateText: useQuantityPricingColumns
						? amountText
						: (purchase ? amountText : formatRateForLegacy(item.rate, mode)),
					amountText,
					hours: mode === 'direct' ? null : item.hours,
					sourceType: item.sourceType || '',
					buildingName: item.buildingName || item.building?.name || '',
				};
			});

			const label = formatDateRange(range.dateFrom, range.dateTo);
			if (buildingScope === 'all') {
				const groupedRows = groupRowsByBuilding(rows);
				sortGroupedRowsByBuildingName(groupedRows).forEach(([buildingName, groupRows]) => {
					groupsForRender.push({
						rangeLabel: label,
						headerText: buildingName,
						rows: groupRows,
					});
				});
			} else {
				groupsForRender.push({
					headerText: buildGroupHeader(resolvedBuildingName, label),
					rows,
				});
			}
		});
	}

	// Convert Custom Dates to Groups
	if (customDates.length > 0) {
		customDates.forEach((entry) => {
			const rows = (entry.items || []).map((item) => {
				const mode = resolveItemAmountMode(item);
				const purchase = isPurchaseItem(item);
				const amountText = formatAmountForLegacy(resolveItemAmountValue(item));
				return {
					description: item.description || item.vendorName || '',
					flatRef: item.flatRef || '',
					hoursText: formatHoursForLegacy(item.hours, mode),
					quantityText: useQuantityPricingColumns || purchase ? '1' : '',
					rateText: useQuantityPricingColumns
						? amountText
						: (purchase ? amountText : formatRateForLegacy(item.rate, mode)),
					amountText,
					hours: mode === 'direct' ? null : item.hours,
					sourceType: item.sourceType || '',
					buildingName: item.buildingName || item.building?.name || '',
				};
			});

			const label = formatDisplayDate(entry.date);
			if (buildingScope === 'all') {
				const groupedRows = groupRowsByBuilding(rows);
				sortGroupedRowsByBuildingName(groupedRows).forEach(([buildingName, groupRows]) => {
					groupsForRender.push({
						rangeLabel: label,
						headerText: buildingName,
						rows: groupRows,
					});
				});
			} else {
				groupsForRender.push({
					headerText: buildGroupHeader(resolvedBuildingName, label),
					rows,
				});
			}
		});
	}

	// Fallback for legacy structure if new structure is empty
	if (groupsForRender.length === 0 && (invoiceData.entryGroups || invoiceData.standaloneGroups)) {
		groupsForRender = [...(invoiceData.entryGroups || []), ...(invoiceData.standaloneGroups || [])];
	}
	if (groupsForRender.length > 0) {
		showFlatRefColumn = groupsForRender.some((group) =>
			(group?.rows || []).some(hasFlatRefColumnData),
		);
		if (!showAppointmentColumns) {
			showAppointmentColumns = groupsForRender.some((group) =>
				(group?.rows || []).some(hasAppointmentColumnData),
			);
		}
		if (!showPurchaseColumns) {
			showPurchaseColumns = groupsForRender.some((group) =>
				(group?.rows || []).some(hasPurchaseColumnData),
			);
		}
	}

	const businessDetailsHtml = formatBusinessInfoHtml(projectInfo?.businessInfo);
	const bankDetailsHtml = escapeMultiLine(projectInfo?.bankDetails || '');

	// Client Details Construction
	const clientName = clientSnapshot.name || invoiceData.clientName || '';
	const clientAddress = clientSnapshot.address || invoiceData.clientAddress || '';
	const clientCustomerId = clientSnapshot.customerId || invoiceData.customerId || '';
	const clientEmail = clientSnapshot.email || invoiceData.clientEmail || invoiceData.email || '';
	const clientTelephone =
		clientSnapshot.telephone ||
		clientSnapshot.phone ||
		invoiceData.clientTelephone ||
		invoiceData.clientPhone ||
		invoiceData.phone ||
		'';

	const clientDetailLines = [];
	if (clientName) clientDetailLines.push(clientName);
	if (clientAddress) clientDetailLines.push(clientAddress);
	if (clientEmail) clientDetailLines.push(clientEmail);
	if (clientTelephone) clientDetailLines.push(clientTelephone);
	if (clientCustomerId) clientDetailLines.push(`Contact: ${clientCustomerId}`);

	if (clientDetailLines.length === 0) {
		clientDetailLines.push('No client selected');
	}

	const clientDetailsHtml = escapeMultiLine(clientDetailLines.join('\n'));

	const logoBlock = effectiveLogoUrl
		? `<img src="${effectiveLogoUrl}" alt="Business Logo" class="business-logo" crossorigin="anonymous" referrerpolicy="no-referrer">`
		: '';

	const notesSectionHtml = notes
		? `
			<div class="notes-section">
				<div style="font-weight: ${CLIENT_DOCUMENT_THEME.tableGroupFontWeight}; font-size: ${CLIENT_DOCUMENT_THEME.bodyFontSize}; color: ${CLIENT_DOCUMENT_THEME.textColor}; margin-bottom: 8px;">Notes</div>
				<div class="notes-content">${escapeMultiLine(notes)}</div>
			</div>`
		: '';

	const bankDetailsSectionHtml = bankDetailsHtml
		? `
			<div class="bank-account-section">
				<div class="bank-account-box">
					<div class="bank-account-title">Bank details</div>
					<div class="bank-account-details">${bankDetailsHtml}</div>
				</div>
			</div>`
		: '';

	return {
		documentKind,
		documentLabel,
		numberLabel,
		dateLabel,
		dueDateLabel,
		purchaseOrderLabel,
		invoiceNumber,
		formattedInvoiceDate,
		formattedDueDate,
		purchaseOrder,
		showDueDate,
		showPurchaseOrder,
		subtotal,
		vatLabel,
		vat,
		total,
		totalTermsText,
		groupsForRender,
		showAppointmentColumns,
		showPurchaseColumns,
		showFlatRefColumn,
		useQuantityPricingColumns,
		clientDetailsHtml,
		businessDetailsHtml,
		logoBlock,
		bankDetailsHtml,
		bankDetailsSectionHtml,
		notes,
		notesSectionHtml,
	};
};

const resolveInvoiceItemColumns = ({
	showAppointmentColumns = true,
	showFlatRefColumn = false,
	showPurchaseColumns = false,
	useQuantityPricingColumns = false,
} = {}) => ({
	showFlatRefColumn,
	showHoursColumn: !useQuantityPricingColumns && showAppointmentColumns,
	showQuantityColumn: useQuantityPricingColumns || showPurchaseColumns,
	showRateColumn: useQuantityPricingColumns || showAppointmentColumns || showPurchaseColumns,
});

const renderGroupRows = (
	groups = [],
	cellPadding = DEFAULT_CELL_PADDING,
	showAppointmentColumns = true,
	showFlatRefColumn = false,
	showPurchaseColumns = false,
	useQuantityPricingColumns = false,
) => {
	const {
		showHoursColumn,
		showQuantityColumn,
		showRateColumn,
	} = resolveInvoiceItemColumns({
		showAppointmentColumns,
		showFlatRefColumn,
		showPurchaseColumns,
		useQuantityPricingColumns,
	});
	const colSpan = 2
		+ (showFlatRefColumn ? 1 : 0)
		+ (showHoursColumn ? 1 : 0)
		+ (showQuantityColumn ? 1 : 0)
		+ (showRateColumn ? 1 : 0);
	const renderRangeHeaderTr = (label) => {
		if (!label) return '';
		return `
			<tr class="range-header-row">
				<td colspan="${colSpan}" style="padding: ${cellPadding} 0 ${cellPadding} 0; font-weight: 700; color: ${CLIENT_DOCUMENT_THEME.textColor}; font-size: ${CLIENT_DOCUMENT_THEME.tableGroupFontSize}; background-color: ${CLIENT_DOCUMENT_THEME.tableRangeBackground}; border-bottom: 1px solid ${CLIENT_DOCUMENT_THEME.ruleColor};">
					${escapeHtml(label)}
				</td>
			</tr>`;
	};

	const renderBuildingHeaderTr = (label) => {
		if (!label) return '';
		return `
			<tr class="date-header-row">
				<td colspan="${colSpan}" style="padding: ${cellPadding} 0 ${cellPadding} 0; font-weight: ${CLIENT_DOCUMENT_THEME.tableGroupFontWeight}; color: ${CLIENT_DOCUMENT_THEME.textColor}; font-size: ${CLIENT_DOCUMENT_THEME.tableGroupFontSize}; background-color: ${CLIENT_DOCUMENT_THEME.tableGroupBackground}; border-bottom: 1px solid ${CLIENT_DOCUMENT_THEME.ruleColor};">
					${escapeHtml(label)}
				</td>
			</tr>`;
	};

	let lastRangeLabel = null;
	return groups
		.map((group) => {
			if (!group || !Array.isArray(group.rows) || group.rows.length === 0) return '';
			let block = '';

			const rangeLabel = (group.rangeLabel || '').trim();
			if (rangeLabel && rangeLabel !== lastRangeLabel) {
				block += renderRangeHeaderTr(rangeLabel);
				lastRangeLabel = rangeLabel;
			}

			block += renderBuildingHeaderTr(group.headerText);

			block += (group.rows || [])
				.map((row) => {
					return `
			<tr>
				<td>${escapeHtml(row.description || '')}</td>
				${showFlatRefColumn ? `<td style="text-align: center;">${escapeHtml(row.flatRef || '')}</td>` : ''}
				${showHoursColumn ? `<td style="text-align: right;">${escapeHtml(row.hoursText || '')}</td>` : ''}
				${showQuantityColumn ? `<td style="text-align: right;">${escapeHtml(row.quantityText || '')}</td>` : ''}
				${showRateColumn ? `<td style="text-align: right;">${escapeHtml(row.rateText || '')}</td>` : ''}
				<td style="text-align: right;">${escapeHtml(row.amountText || '')}</td>
			</tr>`;
				})
				.join('');
			return block;
		})
		.join('');
};

const renderItemsHeaderRow = ({
	showAppointmentColumns = true,
	showFlatRefColumn = false,
	showPurchaseColumns = false,
	useQuantityPricingColumns = false,
} = {}) => {
	const {
		showHoursColumn,
		showQuantityColumn,
		showRateColumn,
	} = resolveInvoiceItemColumns({
		showAppointmentColumns,
		showFlatRefColumn,
		showPurchaseColumns,
		useQuantityPricingColumns,
	});
	const rateColumnLabel = useQuantityPricingColumns
		? 'Unit Price'
		: showAppointmentColumns && showPurchaseColumns
		? 'Rate / Unit Price'
		: (showPurchaseColumns ? 'Unit Price' : 'Rate');
	const rateColumnWidth = !useQuantityPricingColumns && showAppointmentColumns && showPurchaseColumns ? 140 : 100;
	return `
					<tr>
						<th>Description</th>
						${showFlatRefColumn ? '<th style="width: 110px; text-align: center;">Flat Ref</th>' : ''}
						${showHoursColumn ? '<th style="width: 100px; text-align: right;">Hours</th>' : ''}
						${showQuantityColumn ? '<th style="width: 70px; text-align: right;">Qty</th>' : ''}
						${showRateColumn ? `<th style="width: ${rateColumnWidth}px; text-align: right;">${rateColumnLabel}</th>` : ''}
						<th style="width: 120px; text-align: right;">Amount</th>
					</tr>`;
};

const renderTotalsSection = ({ subtotal, vatLabel, vat, total }, cellPadding, sectionMargin) => {
	return `
			<div class="totals-section">
				<table class="totals-table">
					<tr class="subtotal-row">
						<td><strong>Subtotal</strong></td>
						<td style="text-align: right;"><strong>${escapeHtml(subtotal)}</strong></td>
					</tr>
					<tr>
						<td>${escapeHtml(vatLabel || 'VAT (20%)')}</td>
						<td style="text-align: right;">${escapeHtml(vat)}</td>
					</tr>
					<tr class="total-row">
						<td><strong>TOTAL</strong></td>
						<td style="text-align: right;"><strong>${escapeHtml(total)}</strong></td>
					</tr>
				</table>
			</div>`;
};

const renderFooterSplit = ({ bankDetailsHtml, notes }) => {
	return `
				<div class="footer-split-container">
					<div class="footer-split-col">
						<div class="bank-account-section">
							<div class="bank-account-box">
								<div class="bank-account-title">Bank details</div>
								<div class="bank-account-details">${bankDetailsHtml}</div>
							</div>
						</div>
					</div>
					<div class="footer-split-col">
						<div class="notes-section">
							<div style="font-weight: ${CLIENT_DOCUMENT_THEME.tableGroupFontWeight}; font-size: ${CLIENT_DOCUMENT_THEME.bodyFontSize}; color: ${CLIENT_DOCUMENT_THEME.textColor}; margin-bottom: 8px;">Notes</div>
							<div class="notes-content">${escapeMultiLine(notes)}</div>
						</div>
					</div>
				</div>`;
};

const buildTailBlocks = ({ model, spacingOverride, cellPadding, sectionMargin }) => {
	const blocks = [];
	blocks.push({
		key: 'totals',
		html: renderTotalsSection(model, cellPadding, sectionMargin),
	});

	const useSideBySide = Boolean(spacingOverride && spacingOverride.useSideBySide);
	if (useSideBySide && model.notes && model.bankDetailsHtml) {
		blocks.push({
			key: 'footerSplit',
			html: renderFooterSplit(model),
		});
	} else {
		if (model.bankDetailsSectionHtml) {
			blocks.push({ key: 'bankDetails', html: model.bankDetailsSectionHtml });
		}
		if (model.notesSectionHtml) {
			blocks.push({ key: 'notes', html: model.notesSectionHtml });
		}
	}

	if (!spacingOverride || !spacingOverride.hideFooterThanks) {
		blocks.push({
			key: 'thanks',
			html: `
			<div class="footer">
				<p class="footer-thanks">Thank you for your business!</p>
			</div>`,
		});
	}

	return blocks;
};

const buildInvoiceCss = ({ sectionMargin, cellPadding, hideTotalTerms, pageMode }) => {
	const {
		fontFamily,
		textColor,
		secondaryTextColor,
		mutedTextColor,
		ruleColor,
		strongRuleColor,
		tableGroupBackground,
		titleFontSize,
		titleFontWeight,
		bodyFontSize,
		bodyLineHeight,
		tableHeaderFontSize,
		tableHeaderFontWeight,
		tableGroupFontSize,
		tableGroupFontWeight,
		footerFontSize,
	} = CLIENT_DOCUMENT_THEME;
	const pageContainerSizing =
		pageMode === 'multi'
			? `min-height: ${PAGE_HEIGHT}; height: auto; overflow: visible;`
			: `height: ${PAGE_HEIGHT}; overflow: hidden;`;

	return `
			/* Scoped styles for print preview to avoid leaking into the app */
			.print-preview-root {
				background: white;
				margin: 0;
				padding: 0;
				box-sizing: border-box;
			}
			.print-preview-root * { margin: 0; padding: 0; box-sizing: border-box; }
			.pdf-gen-invoice-document { margin: 0; padding: 0; }
			.pdf-gen-invoice-container { font-family: ${fontFamily}; width: ${PAGE_WIDTH}; ${pageContainerSizing} margin: 0 auto; background: white; padding: 40px; box-shadow: none; position: relative; box-sizing: border-box; page-break-after: always; break-after: page; }
			.pdf-gen-invoice-container:last-child { page-break-after: auto; break-after: auto; }
			.pdf-page-end-marker { height: 1px; width: 100%; }
			.pdf-gen-invoice-container .page-number { position: absolute; bottom: 12px; right: 40px; font-size: ${tableHeaderFontSize}; color: ${mutedTextColor}; }
			.pdf-gen-invoice-container::before { content: ''; position: absolute; top: 0; left: 0; width: 100%; height: 3px; }
			.pdf-gen-invoice-container .invoice-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: ${sectionMargin}; }
			.pdf-gen-invoice-container .invoice-title-section { display: flex; flex-direction: column; gap: 8px; }
			.pdf-gen-invoice-container .invoice-title { font-size: ${titleFontSize}; font-weight: ${titleFontWeight}; color: ${textColor}; margin: 0; }
			.pdf-gen-invoice-container .invoice-meta { font-size: ${bodyFontSize}; line-height: ${bodyLineHeight}; }
			.pdf-gen-invoice-container .invoice-meta .label { display: inline-block; width: 115px; color: ${textColor}; font-weight: ${tableHeaderFontWeight}; }
			.pdf-gen-invoice-container .invoice-meta .value { color: ${textColor}; font-weight: ${tableHeaderFontWeight}; }
			.pdf-gen-invoice-container .invoice-meta .invoice-number-row .label,
			.pdf-gen-invoice-container .invoice-meta .invoice-number-row .value { font-weight: 700; }
			.pdf-gen-invoice-container .invoice-continued-header { display: flex; align-items: baseline; justify-content: space-between; gap: 16px; font-size: ${tableHeaderFontSize}; color: ${secondaryTextColor}; font-weight: ${tableGroupFontWeight}; margin-bottom: 16px; }
			.pdf-gen-invoice-container .invoice-continued-page-number { font-weight: ${tableHeaderFontWeight}; color: ${mutedTextColor}; font-variant-numeric: tabular-nums; white-space: nowrap; }
			.pdf-gen-invoice-container .business-logo { max-height: 75px; object-fit: contain; }
			.pdf-gen-invoice-container .business-info-section { }
			.pdf-gen-invoice-container .business-details { white-space: pre-line; line-height: ${bodyLineHeight}; color: ${textColor}; font-size: ${bodyFontSize}; }
			.pdf-gen-invoice-container .bill-to-section { }
			.pdf-gen-invoice-container .bill-to-label { font-weight: bold; color: ${textColor}; font-size: ${bodyFontSize}; }
			.pdf-gen-invoice-container .client-details { white-space: pre-line; line-height: ${bodyLineHeight}; color: ${textColor}; font-size: ${bodyFontSize}; }
			.pdf-gen-invoice-container .billing-row { display: flex; justify-content: space-between; gap: ${sectionMargin}; margin-bottom: ${sectionMargin}; }
			.pdf-gen-invoice-container .billing-row > div { flex: 1; }
			.pdf-gen-invoice-container .total-terms-section { margin-bottom: ${hideTotalTerms ? '0' : sectionMargin}; }
			.pdf-gen-invoice-container .total-terms-text { color: ${textColor}; font-size: 18px; font-weight: bold; line-height: ${bodyLineHeight}; }
			.pdf-gen-invoice-container .items-section { margin-bottom: 20px; }
			/* Scope tables to avoid global leaks */
			.pdf-gen-invoice-container .items-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; color: ${textColor}; font-size: ${bodyFontSize}; table-layout: fixed; }
			.pdf-gen-invoice-container .items-table th { padding: ${cellPadding} 8px; border-bottom: 1px solid ${strongRuleColor}; text-align: left; color: ${textColor}; font-weight: ${tableHeaderFontWeight}; font-size: ${tableHeaderFontSize}; line-height: ${bodyLineHeight}; }
			.pdf-gen-invoice-container .items-table th:first-child { width: auto; padding-left: 0; }
			.pdf-gen-invoice-container .items-table th:last-child { padding-right: 0; }
			.pdf-gen-invoice-container .items-table td { padding: ${cellPadding} 8px; border-bottom: 1px solid ${ruleColor}; text-align: left; color: ${textColor}; font-size: ${bodyFontSize}; line-height: ${bodyLineHeight}; }
			.pdf-gen-invoice-container .items-table td:first-child { width: auto; padding-left: 0; }
			.pdf-gen-invoice-container .items-table td:last-child { padding-right: 0; }
			.pdf-gen-invoice-container .items-table--compact th:nth-child(2),
			.pdf-gen-invoice-container .items-table--compact td:nth-child(2) { width: 120px; text-align: right; }
			.pdf-gen-invoice-container .items-table tbody tr:last-child td { border-bottom: none; }
			.pdf-gen-invoice-container .items-table tr.date-header-row td { padding-left: 0; }
			.pdf-gen-invoice-container .date-header-row td { background-color: ${tableGroupBackground}; font-weight: ${tableGroupFontWeight}; font-size: ${tableGroupFontSize}; }
			.pdf-gen-invoice-container .items-table tr, .pdf-gen-invoice-container .items-table thead { break-inside: avoid; page-break-inside: avoid; }
			.pdf-gen-invoice-container .totals-section { display: flex; justify-content: flex-end; margin-bottom: ${sectionMargin}; }
			.pdf-gen-invoice-container .totals-table { min-width: 250px; border-collapse: collapse; }
			.pdf-gen-invoice-container .totals-table td { padding: ${cellPadding} 15px; border-bottom: 1px solid ${ruleColor}; color: ${textColor}; font-size: ${bodyFontSize}; }
			.pdf-gen-invoice-container .totals-table td:first-child { padding-left: 0; }
			.pdf-gen-invoice-container .totals-table td:last-child { padding-right: 0; }
			.pdf-gen-invoice-container .totals-table .subtotal-row td { border-top: 1px solid ${ruleColor}; }
			.pdf-gen-invoice-container .totals-table .total-row td { color: ${textColor}; font-weight: bold; font-size: ${bodyFontSize}; border-bottom: none; }
			.pdf-gen-invoice-container .bank-account-section { margin-bottom: ${sectionMargin}; }
			.pdf-gen-invoice-container .bank-account-box { border-top: 1px solid ${ruleColor}; padding: 10px 0 0; }
			.pdf-gen-invoice-container .bank-account-title { font-weight: bold; font-size: ${bodyFontSize}; margin-bottom: 0; color: ${textColor}; line-height: 1.6; }
			.pdf-gen-invoice-container .bank-account-details { font-size: ${bodyFontSize}; color: ${textColor}; white-space: pre-line; line-height: 1.6; }
			.pdf-gen-invoice-container .notes-section { margin-bottom: 40px; }
			.pdf-gen-invoice-container .notes-content { padding: 0; white-space: pre-wrap; font-size: ${bodyFontSize}; line-height: ${bodyLineHeight}; color: ${textColor}; }
			.pdf-gen-invoice-container .footer { padding-top: 0; border-top: none; text-align: center; color: ${textColor}; font-size: ${footerFontSize}; }
			.pdf-gen-invoice-container .footer-thanks { font-weight: bold; font-size: ${bodyFontSize}; margin-bottom: 5px; color: ${textColor}; line-height: 1; }
			.pdf-gen-invoice-container .footer-signature { position: absolute; bottom: 40px; left: 40px; font-size: ${bodyFontSize}; color: ${textColor}; }
			.pdf-gen-invoice-container .footer-split-container { display: flex; gap: 40px; margin-bottom: 20px; }
			.pdf-gen-invoice-container .footer-split-col { flex: 1; }
			@media print { body { padding: 0; margin: 0; } .pdf-gen-invoice-container { box-shadow: none; padding: 30px; margin: 0; max-width: none; } .pdf-gen-invoice-container .page-number { right: 30px; } .footer-signature { bottom: 30px; left: 30px; } }
	`;
};

const renderInvoiceHeader = ({ model, hideTotalTerms }) => {
	return `
			<div class="invoice-header">
				<div class="invoice-title-section">
					<h1 class="invoice-title">${escapeHtml(model.documentLabel || 'Invoice')}</h1>
					<div class="invoice-meta">
						<div class="invoice-number-row"><span class="label">${escapeHtml(model.numberLabel || 'Invoice number')}</span><span class="value">${escapeHtml(model.invoiceNumber)}</span></div>
						<div><span class="label">${escapeHtml(model.dateLabel || 'Invoice date')}</span><span class="value">${escapeHtml(model.formattedInvoiceDate)}</span></div>
						${model.showDueDate
							? `<div><span class="label">${escapeHtml(model.dueDateLabel)}</span><span class="value">${escapeHtml(model.formattedDueDate)}</span></div>`
							: ''}
						${model.showPurchaseOrder
							? `<div><span class="label">${escapeHtml(model.purchaseOrderLabel)}</span><span class="value">${escapeHtml(model.purchaseOrder)}</span></div>`
							: ''}
					</div>
				</div>
				<div class="logo-section">${model.logoBlock}</div>
			</div>

			<div class="billing-row">
				<div class="bill-to-section">
					<div class="bill-to-label">Bill To</div>
					<div class="client-details">${model.clientDetailsHtml}</div>
				</div>
				<div class="business-info-section">
					<div class="business-details">${model.businessDetailsHtml}</div>
				</div>
			</div>

			${hideTotalTerms ? '' : `
			<div class="total-terms-section">
				<div class="total-terms-text">${model.totalTermsText}</div>
			</div>
			`}`;
	};

const renderInvoiceContinuedHeader = ({ model, showPageNumbers, pageNumber, totalPages }) => {
	const pageLabel =
		showPageNumbers && totalPages > 1
			? `<span class="invoice-continued-page-number">Page ${pageNumber} of ${totalPages}</span>`
			: '';
	return `
				<div class="invoice-continued-header">
					<span>${escapeHtml(model.documentLabel || 'Invoice')} ${escapeHtml(model.invoiceNumber)}</span>
					${pageLabel}
				</div>`;
};

const renderItemsTable = ({
	groups,
	cellPadding,
	showAppointmentColumns = true,
	showFlatRefColumn = false,
	showPurchaseColumns = false,
	useQuantityPricingColumns = false,
}) => {
	const itemsHtml = renderGroupRows(
		groups,
		cellPadding,
		showAppointmentColumns,
		showFlatRefColumn,
		showPurchaseColumns,
		useQuantityPricingColumns,
	);
	const hasExtraColumns = showAppointmentColumns || showFlatRefColumn || showPurchaseColumns || useQuantityPricingColumns;
	const tableClass = hasExtraColumns ? 'items-table' : 'items-table items-table--compact';
	const headerRow = renderItemsHeaderRow({
		showAppointmentColumns,
		showFlatRefColumn,
		showPurchaseColumns,
		useQuantityPricingColumns,
	});
	return `
			<div class="items-section">
				<table class="${tableClass}">
					<thead>
						${headerRow}
					</thead>
					<tbody>
						${itemsHtml}
					</tbody>
				</table>
			</div>`;
};

const renderInvoicePage = ({
	model,
	variant,
	groups,
	showItemsTable,
	tailBlocks,
	cellPadding,
	hideTotalTerms,
	showAppointmentColumns = true,
	showFlatRefColumn = false,
	showPurchaseColumns = false,
	useQuantityPricingColumns = false,
	showPageNumbers = false,
	pageNumber = 1,
	totalPages = 1,
}) => {
	const headerHtml =
		variant === 'first'
			? renderInvoiceHeader({ model, hideTotalTerms })
			: renderInvoiceContinuedHeader({ model, showPageNumbers, pageNumber, totalPages });

	const itemsHtml = showItemsTable
		? renderItemsTable({
			groups,
			cellPadding,
			showAppointmentColumns,
			showFlatRefColumn,
			showPurchaseColumns,
			useQuantityPricingColumns,
		})
		: '';
	const tailHtml = (tailBlocks || []).map((block) => block?.html || block || '').join('');
	const pageNumberHtml =
		showPageNumbers && totalPages > 1 && variant === 'first'
			? `<div class="page-number">Page ${pageNumber} of ${totalPages}</div>`
			: '';

	return `
		<div class="pdf-gen-invoice-container">
			${headerHtml}
			${itemsHtml}
			${tailHtml}
			${pageNumberHtml}
			<div class="pdf-page-end-marker" data-page-end-marker="true"></div>
		</div>`;
};

const renderInvoiceDocument = ({
	model,
	pages,
	styles,
	spacingOverride,
	cellPadding,
	hideTotalTerms,
}) => {
	const totalPages = Array.isArray(pages) ? pages.length : 0;
	const showPageNumbers = totalPages > 1;
	const showAppointmentColumns = model?.showAppointmentColumns !== false;
	const showFlatRefColumn = model?.showFlatRefColumn === true;
	const showPurchaseColumns = model?.showPurchaseColumns === true;
	const useQuantityPricingColumns = model?.useQuantityPricingColumns === true;
	const pagesHtml = (pages || [])
		.map((page, index) =>
			renderInvoicePage({
				model,
				variant: page.variant,
				groups: page.groups || [],
				showItemsTable: page.showItemsTable,
				tailBlocks: page.tailBlocks || [],
				cellPadding,
				hideTotalTerms: page.variant === 'first' ? hideTotalTerms : true,
				showPageNumbers,
				pageNumber: index + 1,
				totalPages,
				showAppointmentColumns,
				showFlatRefColumn,
				showPurchaseColumns,
				useQuantityPricingColumns,
			}),
		)
		.join('');

	return `<!DOCTYPE html>
	<html lang="en">
	<head>
		<meta charset="UTF-8">
		<meta name="viewport" content="width=device-width, initial-scale=1.0">
		<title>${escapeHtml(model.documentLabel || 'Invoice')} ${escapeHtml(model.invoiceNumber)}</title>
		<style>
${styles}
		</style>
	</head>
	<body class="print-preview-root">
		<div class="pdf-gen-invoice-document">
${pagesHtml}
		</div>
	</body>
	</html>`;
};

export const generateInvoiceHtml = ({
	invoice,
	projectInfo,
	businessLogoUrl, // Legacy uses this
	logoUrl, // New code passes this
	spacingOverride,
	snapshot, // New code passes this
	hideTotalTerms = false, // New option to hide total terms section
	pageMode = 'single', // 'single' | 'multi'
}) => {
	const sectionMargin = spacingOverride?.sectionMargin || DEFAULT_SECTION_MARGIN;
	const cellPadding = spacingOverride?.cellVerticalPadding || DEFAULT_CELL_PADDING;
	const model = buildInvoiceModel({
		invoice,
		projectInfo,
		businessLogoUrl,
		logoUrl,
		snapshot,
		hideTotalTerms,
	});

	const styles = buildInvoiceCss({ sectionMargin, cellPadding, hideTotalTerms, pageMode });
	const tailBlocks = buildTailBlocks({ model, spacingOverride, cellPadding, sectionMargin });
	const pages = [
		{
			variant: 'first',
			groups: model.groupsForRender || [],
			showItemsTable: true,
			tailBlocks,
		},
	];

	return renderInvoiceDocument({
		model,
		pages,
		styles,
		spacingOverride,
		cellPadding,
		hideTotalTerms,
	});
};

export const generateInvoiceHtmlWithAutoSpacing = async ({
	invoice,
	projectInfo,
	businessLogoUrl,
	logoUrl,
	snapshot,
	initialSpacing = null,
	spacingCandidates = SPACING_PRESETS,
}) => {
	if (typeof window === 'undefined' || typeof document === 'undefined') {
		return {
			html: generateInvoiceHtml({
				invoice,
				projectInfo,
				businessLogoUrl,
				logoUrl,
				snapshot,
				pageMode: 'multi',
				spacingOverride: initialSpacing || null,
			}),
			spacing: initialSpacing || null,
		};
	}

	const spacingQueue = normaliseSpacingQueue(initialSpacing, spacingCandidates);
	await waitForClientDocumentFonts(document);

	const probe = createProbeContainer();
	document.body.appendChild(probe);

	const shadowRoot = probe.shadowRoot || probe.attachShadow({ mode: 'open' });

	const pageFitsWithBuffer = (container, bufferPx = FOOTER_BUFFER_PX) => {
		if (!container) return true;
		if (!bufferPx) {
			return container.scrollHeight <= container.clientHeight;
		}
		const containerRect = container.getBoundingClientRect();
		const marker = container.querySelector('[data-page-end-marker]') || container.lastElementChild;
		if (!marker) return true;
		const markerRect = marker.getBoundingClientRect();
		return markerRect.bottom <= containerRect.bottom - bufferPx + 0.5;
	};

	// Helper to check overflow with some safety margin and measurements for debugging/fallbacks
	const checkOverflow = async (html) => {
		shadowRoot.innerHTML = html;
		const container = shadowRoot.querySelector('.pdf-gen-invoice-container');
		if (!container) {
			return { fits: true, overflowPx: 0, footerOverflowPx: -FOOTER_BUFFER_PX, tailOverflowPx: 0 };
		}
		await waitForImages(container);
		const overflow = container.scrollHeight - container.clientHeight;
		const footer = container.querySelector('.footer');
		let footerOverflow = -FOOTER_BUFFER_PX;
		const containerRect = container.getBoundingClientRect();
		if (footer) {
			const footerRect = footer.getBoundingClientRect();
			footerOverflow = footerRect.bottom - containerRect.bottom;
		}
		// Check if last element is cut off
		const lastEl = container.lastElementChild;
		let tailOverflow = 0;
		if (lastEl) {
			const lastElRect = lastEl.getBoundingClientRect();
			tailOverflow = lastElRect.bottom - containerRect.bottom;
		}
		const fits = overflow <= 0 && footerOverflow <= -FOOTER_BUFFER_PX && tailOverflow <= 0;
		return { fits, overflowPx: overflow, footerOverflowPx: footerOverflow, tailOverflowPx: tailOverflow };
	};

	const renderRangeHeaderTr = (
		label,
		cellPadding,
		showAppointmentColumns = true,
		showFlatRefColumn = false,
		showPurchaseColumns = false,
		useQuantityPricingColumns = false,
	) => {
		if (!label) return '';
		const {
			showHoursColumn,
			showQuantityColumn,
			showRateColumn,
		} = resolveInvoiceItemColumns({
			showAppointmentColumns,
			showFlatRefColumn,
			showPurchaseColumns,
			useQuantityPricingColumns,
		});
		const colSpan = 2
			+ (showFlatRefColumn ? 1 : 0)
			+ (showHoursColumn ? 1 : 0)
			+ (showQuantityColumn ? 1 : 0)
			+ (showRateColumn ? 1 : 0);
		return `
			<tr class="range-header-row">
				<td colspan="${colSpan}" style="padding: ${cellPadding} 0 ${cellPadding} 0; font-weight: 700; color: ${CLIENT_DOCUMENT_THEME.textColor}; font-size: ${CLIENT_DOCUMENT_THEME.tableGroupFontSize}; background-color: ${CLIENT_DOCUMENT_THEME.tableRangeBackground}; border-bottom: 1px solid ${CLIENT_DOCUMENT_THEME.ruleColor};">
					${escapeHtml(label)}
				</td>
			</tr>`;
	};

	const renderBuildingHeaderTr = (
		label,
		cellPadding,
		showAppointmentColumns = true,
		showFlatRefColumn = false,
		showPurchaseColumns = false,
		useQuantityPricingColumns = false,
	) => {
		if (!label) return '';
		const {
			showHoursColumn,
			showQuantityColumn,
			showRateColumn,
		} = resolveInvoiceItemColumns({
			showAppointmentColumns,
			showFlatRefColumn,
			showPurchaseColumns,
			useQuantityPricingColumns,
		});
		const colSpan = 2
			+ (showFlatRefColumn ? 1 : 0)
			+ (showHoursColumn ? 1 : 0)
			+ (showQuantityColumn ? 1 : 0)
			+ (showRateColumn ? 1 : 0);
		return `
			<tr class="date-header-row">
				<td colspan="${colSpan}" style="padding: ${cellPadding} 0 ${cellPadding} 0; font-weight: ${CLIENT_DOCUMENT_THEME.tableGroupFontWeight}; color: ${CLIENT_DOCUMENT_THEME.textColor}; font-size: ${CLIENT_DOCUMENT_THEME.tableGroupFontSize}; background-color: ${CLIENT_DOCUMENT_THEME.tableGroupBackground}; border-bottom: 1px solid ${CLIENT_DOCUMENT_THEME.ruleColor};">
					${escapeHtml(label)}
				</td>
			</tr>`;
	};

	const renderItemTr = (
		row,
		showAppointmentColumns = true,
		showFlatRefColumn = false,
		showPurchaseColumns = false,
		useQuantityPricingColumns = false,
	) => {
		const {
			showHoursColumn,
			showQuantityColumn,
			showRateColumn,
		} = resolveInvoiceItemColumns({
			showAppointmentColumns,
			showFlatRefColumn,
			showPurchaseColumns,
			useQuantityPricingColumns,
		});
		return `
			<tr>
				<td>${escapeHtml(row.description || '')}</td>
				${showFlatRefColumn ? `<td style="text-align: center;">${escapeHtml(row.flatRef || '')}</td>` : ''}
				${showHoursColumn ? `<td style="text-align: right;">${escapeHtml(row.hoursText || '')}</td>` : ''}
				${showQuantityColumn ? `<td style="text-align: right;">${escapeHtml(row.quantityText || '')}</td>` : ''}
				${showRateColumn ? `<td style="text-align: right;">${escapeHtml(row.rateText || '')}</td>` : ''}
				<td style="text-align: right;">${escapeHtml(row.amountText || '')}</td>
			</tr>`;
	};

	const mountItemsPageSkeleton = async ({
		model,
		styles,
		variant,
		cellPadding,
		hideTotalTerms,
		showAppointmentColumns = true,
		showFlatRefColumn = false,
		showPurchaseColumns = false,
		useQuantityPricingColumns = false,
	}) => {
		const hasExtraColumns = showAppointmentColumns || showFlatRefColumn || showPurchaseColumns || useQuantityPricingColumns;
		const tableClass = hasExtraColumns ? 'items-table' : 'items-table items-table--compact';
		const headerRow = renderItemsHeaderRow({
			showAppointmentColumns,
			showFlatRefColumn,
			showPurchaseColumns,
			useQuantityPricingColumns,
		});
		const itemsTableShell = `
			<div class="items-section">
				<table class="${tableClass}">
					<thead>
						${headerRow}
					</thead>
					<tbody></tbody>
				</table>
			</div>`;

		shadowRoot.innerHTML = `
			<style>${styles}</style>
			<div class="pdf-gen-invoice-container">
				${variant === 'first'
					? renderInvoiceHeader({ model, hideTotalTerms })
					: renderInvoiceContinuedHeader({ model })}
				${itemsTableShell}
				<div class="pdf-page-end-marker" data-page-end-marker="true"></div>
			</div>`;

		const container = shadowRoot.querySelector('.pdf-gen-invoice-container');
		const tbody = shadowRoot.querySelector('.items-table tbody');
		await waitForImages(container);
		return { container, tbody };
	};

	const measurePage = async ({
		model,
		styles,
		page,
		cellPadding,
		hideTotalTerms,
		showAppointmentColumns = true,
		showFlatRefColumn = false,
		showPurchaseColumns = false,
		useQuantityPricingColumns = false,
	}) => {
		shadowRoot.innerHTML = `
			<style>${styles}</style>
			${renderInvoicePage({
				model,
				variant: page.variant,
				groups: page.groups || [],
				showItemsTable: page.showItemsTable,
				tailBlocks: page.tailBlocks || [],
				cellPadding,
				hideTotalTerms: page.variant === 'first' ? hideTotalTerms : true,
				showAppointmentColumns,
				showFlatRefColumn,
				showPurchaseColumns,
				useQuantityPricingColumns,
			})}`;

		const container = shadowRoot.querySelector('.pdf-gen-invoice-container');
		await waitForImages(container);
		return pageFitsWithBuffer(container, FOOTER_BUFFER_PX);
	};

	let selectedSpacing = null;
	let selectedHtml = null;

	try {
		// 1) First try to fit in a single page by reducing paddings
		for (const spacing of spacingQueue) {
			const html = generateInvoiceHtml({
				invoice,
				projectInfo,
				businessLogoUrl,
				logoUrl,
				snapshot,
				pageMode: 'single',
				spacingOverride: spacing || null,
			});
			const { fits } = await checkOverflow(html);
			if (fits) {
				selectedSpacing = spacing || null;
				selectedHtml = html;
				break;
			}
		}

		if (selectedHtml) {
			return { html: selectedHtml, spacing: selectedSpacing };
		}

		// 2) Still doesn't fit: keep reduced paddings and paginate into A4 pages
		selectedSpacing = pickMostCompactSpacing(spacingQueue) || initialSpacing || null;
		const sectionMargin = selectedSpacing?.sectionMargin || DEFAULT_SECTION_MARGIN;
		const cellPadding = selectedSpacing?.cellVerticalPadding || DEFAULT_CELL_PADDING;

		const model = buildInvoiceModel({
			invoice,
			projectInfo,
			businessLogoUrl,
			logoUrl,
			snapshot,
			hideTotalTerms: false,
		});
		const styles = buildInvoiceCss({
			sectionMargin,
			cellPadding,
			hideTotalTerms: false,
			pageMode: 'single',
		});

		const showAppointmentColumns = model?.showAppointmentColumns !== false;
		const showFlatRefColumn = model?.showFlatRefColumn === true;
		const showPurchaseColumns = model?.showPurchaseColumns === true;
		const useQuantityPricingColumns = model?.useQuantityPricingColumns === true;
		const pages = [];
		const groups = Array.isArray(model.groupsForRender) ? model.groupsForRender : [];
		let groupIndex = 0;
		let rowIndex = 0;

		const nextNonEmptyGroupIndex = (startIndex) => {
			let idx = startIndex;
			while (idx < groups.length) {
				const group = groups[idx];
				if (group && Array.isArray(group.rows) && group.rows.length > 0) return idx;
				idx += 1;
			}
			return idx;
		};

		groupIndex = nextNonEmptyGroupIndex(0);

		while (groupIndex < groups.length) {
			const variant = pages.length === 0 ? 'first' : 'continued';
			const { container, tbody } = await mountItemsPageSkeleton({
				model,
				styles,
				variant,
				cellPadding,
				hideTotalTerms: false,
				showAppointmentColumns,
				showFlatRefColumn,
				showPurchaseColumns,
				useQuantityPricingColumns,
			});

			const pageGroups = [];
			let currentPageGroupIndex = null;
			let currentPageRangeLabel = null;

			while (groupIndex < groups.length) {
				const group = groups[groupIndex];
				if (!group || !Array.isArray(group.rows) || group.rows.length === 0) {
					groupIndex = nextNonEmptyGroupIndex(groupIndex + 1);
					rowIndex = 0;
					currentPageGroupIndex = null;
					continue;
				}

				const row = group.rows[rowIndex];
				if (!row) {
					groupIndex = nextNonEmptyGroupIndex(groupIndex + 1);
					rowIndex = 0;
					currentPageGroupIndex = null;
					continue;
				}

				const rangeLabel = (group.rangeLabel || '').trim();
				const shouldInsertRangeHeader = rangeLabel && rangeLabel !== currentPageRangeLabel;
				const rangeHeaderTr = shouldInsertRangeHeader
					? renderRangeHeaderTr(
						rangeLabel,
						cellPadding,
						showAppointmentColumns,
						showFlatRefColumn,
						showPurchaseColumns,
						useQuantityPricingColumns,
					)
					: '';

				const isFirstRowOfGroupOnThisPage = currentPageGroupIndex !== groupIndex;
				const buildingHeaderTr = isFirstRowOfGroupOnThisPage
					? renderBuildingHeaderTr(
						group.headerText,
						cellPadding,
						showAppointmentColumns,
						showFlatRefColumn,
						showPurchaseColumns,
						useQuantityPricingColumns,
					)
					: '';
				const rowTr = renderItemTr(
					row,
					showAppointmentColumns,
					showFlatRefColumn,
					showPurchaseColumns,
					useQuantityPricingColumns,
				);

				const beforeCount = tbody.children.length;
				tbody.insertAdjacentHTML('beforeend', `${rangeHeaderTr}${buildingHeaderTr}${rowTr}`);
				const insertedCount = tbody.children.length - beforeCount;

				let fits = pageFitsWithBuffer(container, FOOTER_BUFFER_PX);
				if (!fits && pageGroups.length === 0) {
					fits = pageFitsWithBuffer(container, 0);
				}

					if (!fits) {
						for (let i = 0; i < insertedCount; i += 1) {
							tbody.lastElementChild?.remove();
						}
						// If even the very first row doesn't fit (rare), force it onto the page to avoid dropping data.
						if (pageGroups.length === 0) {
							tbody.insertAdjacentHTML('beforeend', `${rangeHeaderTr}${buildingHeaderTr}${rowTr}`);
							pageGroups.push({
								rangeLabel: group.rangeLabel,
								headerText: group.headerText,
								rows: [row],
							});
							currentPageGroupIndex = groupIndex;
							if (shouldInsertRangeHeader) {
								currentPageRangeLabel = rangeLabel;
							}

							rowIndex += 1;
							if (rowIndex >= group.rows.length) {
								groupIndex = nextNonEmptyGroupIndex(groupIndex + 1);
								rowIndex = 0;
								currentPageGroupIndex = null;
							}
						}
						break;
					}

				if (isFirstRowOfGroupOnThisPage) {
					pageGroups.push({
						rangeLabel: group.rangeLabel,
						headerText: group.headerText,
						rows: [row],
					});
					currentPageGroupIndex = groupIndex;
				} else {
					pageGroups[pageGroups.length - 1].rows.push(row);
				}
				if (shouldInsertRangeHeader) {
					currentPageRangeLabel = rangeLabel;
				}

				rowIndex += 1;
				if (rowIndex >= group.rows.length) {
					groupIndex = nextNonEmptyGroupIndex(groupIndex + 1);
					rowIndex = 0;
					currentPageGroupIndex = null;
				}
			}

				pages.push({
					variant,
					groups: pageGroups,
					showItemsTable: true,
					tailBlocks: [],
				});
			}

		if (pages.length === 0) {
			pages.push({
				variant: 'first',
				groups: [],
				showItemsTable: true,
				tailBlocks: [],
			});
		}

		const tailBlocks = buildTailBlocks({ model, spacingOverride: selectedSpacing, cellPadding, sectionMargin });
		let targetPageIndex = pages.length - 1;

		for (const block of tailBlocks) {
			let placed = false;
			let guard = 0;
			while (!placed && guard < 20) {
				guard += 1;
				const page = pages[targetPageIndex];
				const candidatePage = {
					...page,
					tailBlocks: [...(page.tailBlocks || []), block],
				};

				const fits = await measurePage({
					model,
					styles,
					page: candidatePage,
					cellPadding,
					hideTotalTerms: false,
					showAppointmentColumns,
					showFlatRefColumn,
					showPurchaseColumns,
					useQuantityPricingColumns,
				});

				if (fits) {
					page.tailBlocks.push(block);
					placed = true;
					break;
				}

				pages.push({
					variant: 'continued',
					groups: [],
					showItemsTable: false,
					tailBlocks: [],
				});
				targetPageIndex = pages.length - 1;
			}

			if (!placed) {
				pages[targetPageIndex].tailBlocks.push(block);
			}
		}

		selectedHtml = renderInvoiceDocument({
			model,
			pages,
			styles,
			spacingOverride: selectedSpacing,
			cellPadding,
			hideTotalTerms: false,
		});

		return { html: selectedHtml, spacing: selectedSpacing };
	} finally {
		probe.remove();
	}
};

export const deriveInvoiceFilename = (invoice) => {
	const invoiceData = invoice?.data || invoice || {};
	const documentKind = resolveInvoiceDocumentKind(invoiceData);
	const numberRaw = invoiceData.invoiceNumber || invoice?.name || `${Date.now()}`;
	const cleanedNumber = String(numberRaw).replace(/[^a-zA-Z0-9-_]/g, '').trim() || `${Date.now()}`;
	const dateObj = safeDate(invoiceData.invoiceDate);
	const dateVal = dateObj ? format(dateObj, 'yyyy-MM-dd') : '';
	const sanitizeSegment = (value) => {
		if (!value) return '';
		return String(value)
			.replace(/[\\/:*?"<>|]+/g, ' ')
			.replace(/\s+/g, ' ')
			.trim();
	};
	
	// Try to find client name in various places
	const client = sanitizeSegment(
		invoiceData.clientName || 
		(invoiceData.clientSnapshot && invoiceData.clientSnapshot.name)
	);
	
	// Try to find building name
	const building = sanitizeSegment(
		invoiceData.buildingName || 
		(invoiceData.clientSnapshot && invoiceData.clientSnapshot.buildingName)
	);
	
	const prefix = documentKind === 'credit-note' ? 'Credit-Note' : 'Invoice';
	const parts = [`${prefix}-${cleanedNumber}`];
	if (dateVal) parts.push(dateVal);
	if (client) parts.push(client);
	if (building) parts.push(building);
	return `${parts.join(' ')}.pdf`;
};
